import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RateCalculator = () => {
  // Progress/Step State
  const [currentStep, setCurrentStep] = useState(0); // Start at 0 for email verification
  const totalSteps = 5; // Increased from 4 to 5 (added email verification step)
  const [sessionId] = useState(() => Math.random().toString(36).substring(7));
  const [isPortalUser, setIsPortalUser] = useState(false);

  // Email Verification State
  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  const [formData, setFormData] = useState({
    // Customer Information
    customerName: '',
    customerEmail: '',
    customerCompany: '',
    customerPhone: '',
    // Shipment Information
    pickupLocation: '',
    deliveryLocation: '',
    shipmentType: 'import', // import, export, trailer
    containerNumber: '',
    unloadType: 'live', // live or drop
    containerType: '',
    cargoWeight: '',
    cargoWeightUnit: 'lbs',
    chassisDays: '',
    triaxleDays: '',
    storageDays: '',
    detentionHours: '',
    overweight: false,
    reefer: false,
    portLibertyBayonneToll: false,
    portLibertyNYToll: false,
  });

  // Check for portal authentication and auto-fill user data
  useEffect(() => {
    const checkPortalAuth = async () => {
      const portalToken = localStorage.getItem('portal_token');
      const portalEmail = localStorage.getItem('portal_email');
      
      if (portalToken && portalEmail) {
        try {
          // Fetch user profile
          const response = await axios.get(`${BACKEND_URL}/api/portal/profile`, {
            headers: {
              'Authorization': `Bearer ${portalToken}`
            }
          });
          
          const profile = response.data;
          
          // Auto-fill customer information
          setFormData(prev => ({
            ...prev,
            customerName: profile.name || '',
            customerEmail: profile.email || portalEmail,
            customerCompany: profile.company_name || '',
            customerPhone: profile.phone || ''
          }));
          
          // Set email and mark as verified
          setEmail(profile.email || portalEmail);
          setEmailVerified(true);
          setIsPortalUser(true);
          
          // Skip email verification step and go to step 1
          setCurrentStep(1);
          
          console.log('✅ Portal user detected - auto-filled profile and skipped email verification');
        } catch (error) {
          console.error('Failed to load portal profile:', error);
          // If token is invalid, clear it
          if (error.response?.status === 401) {
            localStorage.removeItem('portal_token');
            localStorage.removeItem('portal_email');
          }
        }
      }
    };
    
    checkPortalAuth();
  }, []);

  // Check for URL parameters and prefill form
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pickup = params.get('pickup');
    const delivery = params.get('delivery');
    const container = params.get('container');

    // Map homepage container labels to form values
    const containerMapping = {
      "20' Standard": '20ft',
      "40' Standard": '40ft',
      "40' High Cube": '40ft_hc',
      "45' High Cube": '45ft'
    };

    if (pickup || delivery || container) {
      setFormData(prev => ({
        ...prev,
        ...(pickup && { pickupLocation: pickup }),
        ...(delivery && { deliveryLocation: delivery }),
        ...(container && { containerType: containerMapping[container] || container })
      }));
    }
  }, []);

  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDeliverySuggestions, setShowDeliverySuggestions] = useState(false);
  const [filteredPickupLocations, setFilteredPickupLocations] = useState([]);
  const [filteredDeliveryLocations, setFilteredDeliveryLocations] = useState([]);

  const [calculating, setCalculating] = useState(false);
  const [rateResult, setRateResult] = useState(null);
  const [error, setError] = useState('');
  
  // PDF generation states
  const [pdfFilename, setPdfFilename] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [pdfMessage, setPdfMessage] = useState('');

  // Booking modal states
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingData, setBookingData] = useState({
    vesselEta: '',
    terminalReceivingDate: '',
    terminalCutOffDate: '',
    containerTrailerNumbers: [{number: ''}],
    billOfLadingNumbers: [{number: ''}],
    pickupDate: '',
    pickupTime: '',
    deliveryDate: '',
    deliveryTime: '',
    specialInstructions: '',
    preferredContactMethod: 'email',
    additionalContactPerson: '',
    customerPhone: '',
    notificationEmail: ''  // Email for receiving booking updates
  });
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);

  // Email validation states
  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    message: '',
    suggestion: '',
    showWarning: false
  });

  // Comprehensive Email Validation Function
  const validateEmailComprehensive = (email) => {
    // Blocked domains (fake/test emails)
    const blockedDomains = [
      'test.com', 'example.com', 'fake.com', 'temp.com', 'throwaway.com',
      'abc.com', 'xyz.com', 'asdf.com', 'qwerty.com', 'mail.com',
      'a.com', 'b.com', 'c.com', 'd.com', 'e.com', 'f.com', 'g.com',
      'x.com', 'y.com', 'z.com', '123.com'
    ];

    // Common typo corrections
    const commonTypos = {
      'gmail.con': 'gmail.com',
      'gmail.cm': 'gmail.com',
      'gmail.comm': 'gmail.com',
      'gmai.com': 'gmail.com',
      'gmial.com': 'gmail.com',
      'yahooo.com': 'yahoo.com',
      'yaho.com': 'yahoo.com',
      'yahoo.comm': 'yahoo.com',
      'hotmial.com': 'hotmail.com',
      'hotmai.com': 'hotmail.com',
      'hotmail.comm': 'hotmail.com',
      'outlok.com': 'outlook.com',
      'outloo.com': 'outlook.com',
      'outlook.comm': 'outlook.com'
    };

    if (!email) {
      return { isValid: false, message: '', suggestion: '', showWarning: false };
    }

    // Basic format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        isValid: false,
        message: 'Invalid email format',
        suggestion: '',
        showWarning: true
      };
    }

    const emailLower = email.toLowerCase();
    const [localPart, domain] = emailLower.split('@');

    // Check for single letter local part (e.g., a@domain.com)
    if (localPart.length === 1) {
      return {
        isValid: false,
        message: 'Email address is too short. Please enter your full email.',
        suggestion: '',
        showWarning: true
      };
    }

    // Check for single letter domains or extremely short domains
    const domainParts = domain.split('.');
    if (domainParts[0].length <= 2 && !['co', 'me', 'io', 'uk', 'us', 'ca'].includes(domainParts[0])) {
      return {
        isValid: false,
        message: 'This appears to be a fake email address. Please enter your real email.',
        suggestion: '',
        showWarning: true
      };
    }

    // Check blocked domains
    if (blockedDomains.includes(domain)) {
      return {
        isValid: false,
        message: 'This email domain is not accepted. Please use your business or personal email.',
        suggestion: '',
        showWarning: true
      };
    }

    // Check for common typos and suggest corrections
    for (const [typo, correction] of Object.entries(commonTypos)) {
      if (domain === typo) {
        return {
          isValid: false,
          message: `Did you mean ${localPart}@${correction}?`,
          suggestion: `${localPart}@${correction}`,
          showWarning: true
        };
      }
    }

    // Check for sequential characters (e.g., abc@abc.com, test@test.com)
    if (localPart === domainParts[0]) {
      return {
        isValid: false,
        message: 'This appears to be a test email. Please enter your real email address.',
        suggestion: '',
        showWarning: true
      };
    }

    // Check for very short domain extensions
    const extension = domainParts[domainParts.length - 1];
    if (extension.length < 2) {
      return {
        isValid: false,
        message: 'Invalid email domain. Please check your email address.',
        suggestion: '',
        showWarning: true
      };
    }

    // Valid TLD list (common top-level domains)
    const validTLDs = [
      'com', 'net', 'org', 'edu', 'gov', 'mil', 'int',
      'co', 'us', 'uk', 'ca', 'au', 'de', 'jp', 'fr', 'it', 'nl', 'es', 'ru', 'ch', 'se', 'no', 'dk',
      'io', 'ai', 'me', 'tv', 'cc', 'ws', 'biz', 'info', 'mobi', 'name', 'asia', 'tel',
      'travel', 'jobs', 'pro', 'aero', 'coop', 'museum'
    ];

    // Check if TLD is valid
    if (!validTLDs.includes(extension)) {
      // Check if it might be a typo of a valid TLD
      if (extension.endsWith('comm') || extension.endsWith('con') || extension.endsWith('cm')) {
        const possibleCorrection = extension.slice(0, -1); // Remove last character
        if (validTLDs.includes(possibleCorrection)) {
          const correctedEmail = `${localPart}@${domainParts.slice(0, -1).join('.')}.${possibleCorrection}`;
          return {
            isValid: false,
            message: `Did you mean ${correctedEmail}?`,
            suggestion: correctedEmail,
            showWarning: true
          };
        }
      }
      
      return {
        isValid: false,
        message: 'Invalid email domain extension. Please check your email address.',
        suggestion: '',
        showWarning: true
      };
    }

    // Valid email
    return {
      isValid: true,
      message: 'Valid email address',
      suggestion: '',
      showWarning: false
    };
  };

  // Container types and base rates
  const containerTypes = [
    { value: '20ft', label: '20ft Standard Container', baseRate: 350 },
    { value: '40ft', label: '40ft Standard Container', baseRate: 450 },
    { value: '40ft_hc', label: '40ft High Cube Container', baseRate: 475 },
    { value: '45ft', label: '45ft High Cube Container', baseRate: 525 },
    { value: 'reefer_20ft', label: '20ft Reefer Container', baseRate: 550 },
    { value: 'reefer_40ft', label: '40ft Reefer Container', baseRate: 650 },
  ];

  const serviceTypes = [
    { value: 'standard', label: 'Standard Service (2-3 days)', multiplier: 1.0 },
    { value: 'expedited', label: 'Expedited Service (1-2 days)', multiplier: 1.3 },
    { value: 'same_day', label: 'Same Day Service', multiplier: 1.8 },
  ];

  // Major port/terminal locations with coordinates
  const locations = {
    // Terminals
    'C&C MAINTENANCE: 88 MARSH STREET NEWARK, NJ 07114': { lon: -74.1520, lat: 40.7246 },
    'CCS: 1155 POLARIS STREET ELIZABETH, NJ 07201': { lon: -74.1689, lat: 40.6652 },
    'COLUMBIA CONTAINER SERVICES: 1100 POLARIS STREET ELIZABETH, NJ 07201': { lon: -74.1694, lat: 40.6657 },
    'CSX KEARNY: 700 OLD FISH HOUSE ROAD KEARNY, NJ 07032': { lon: -74.1124, lat: 40.7589 },
    'CSX NORTH BERGEN: 6201 TONNELLE AVENUE NORTH BERGEN, NJ 07047': { lon: -74.0271, lat: 40.7923 },
    'EAST COAST WAREHOUSE (CES): 141 NORTH AVE ELISABETH, NJ 07021': { lon: -74.2079, lat: 40.6642 },
    'ELIZABETH CHASSIS DEPOT: 1510 S BAY AVE ELIZABETH, NJ 07201': { lon: -74.1558, lat: 40.6521 },
    'PORT LIBERTY BAYONNE: 302 PORT JERSEY BLVD JERSEY CITY, NJ 07305': { lon: -74.0589, lat: 40.7012 },
    'APM TERMINAL – BERTH 88: 5080 MCLESTER STREET PORT ELIZABETH, NJ 07114': { lon: -74.1578, lat: 40.6698 },
    'H&M: 700 BELLEVILLE TPKE KEARNY, NJ 07032': { lon: -74.1245, lat: 40.7612 },
    'INTERPORT: 635 DELANCY STREET NEWARK, NJ 07105': { lon: -74.1589, lat: 40.7289 },
    'MAHER TERMINAL - BETH 64: 1210 CORBIN ST ELIZABETH, NJ 07201': { lon: -74.1625, lat: 40.6589 },
    'PORT LIBERTY NEW YORK: 300 WESTERN AVENUE STATEN ISLAND, NY 10303': { lon: -74.1789, lat: 40.6345 },
    'PORT NEWARK CONTAINER TERMINAL - PNCT: 241 CALCULATTA STREET NEWARK, NJ 07101': { lon: -74.1456, lat: 40.6925 },
    'RED HOOK - NEWARK: 138 MARCH STREET NEWARK, NJ 07114': { lon: -74.1534, lat: 40.7258 },
    'RED HOOK - BROOKLYN: 70 HAMILTON AVE BROOKLYN, NY 11231': { lon: -74.0089, lat: 40.6742 },
    
    // General Port/Terminal Options (uses MAHER coordinates for distance calculation)
    'NY/NJ PORTS': { lon: -74.1625, lat: 40.6589 },
    'NY/NJ TERMINAL': { lon: -74.1625, lat: 40.6589 },
    'NY/NJ TERMINALS': { lon: -74.1625, lat: 40.6589 },
    'NYNJ PORTS': { lon: -74.1625, lat: 40.6589 },
    'NYNJ TERMINAL': { lon: -74.1625, lat: 40.6589 },
    'NYNJ TERMINALS': { lon: -74.1625, lat: 40.6589 },
    
    // Major Ports
    'Newark Port, NJ': { lon: -74.1415, lat: 40.6828 },
    'Elizabeth Port, NJ': { lon: -74.1602, lat: 40.6631 },
    'Port Jersey, NJ': { lon: -74.0456, lat: 40.7009 },
    'Brooklyn Port, NY': { lon: -74.0060, lat: 40.6782 },
    'Philadelphia Port, PA': { lon: -75.1314, lat: 39.9126 },
    'Wilmington Port, DE': { lon: -75.5398, lat: 39.7391 },
    
    // Major Cities
    'New York, NY': { lon: -74.0060, lat: 40.7128 },
    'Jersey City, NJ': { lon: -74.0431, lat: 40.7178 },
    'Hoboken, NJ': { lon: -74.0321, lat: 40.7439 },
    'Edison, NJ': { lon: -74.4121, lat: 40.5187 },
    'Trenton, NJ': { lon: -74.7429, lat: 40.2171 },
    'Camden, NJ': { lon: -75.1196, lat: 39.9259 },
    'Philadelphia, PA': { lon: -75.1652, lat: 39.9526 },
    'Allentown, PA': { lon: -75.4714, lat: 40.6084 },
    'Pittsburgh, PA': { lon: -79.9959, lat: 40.4406 },
    'Hartford, CT': { lon: -72.6819, lat: 41.7658 },
    'New Haven, CT': { lon: -72.9279, lat: 41.3083 },
    'Bridgeport, CT': { lon: -73.1951, lat: 41.1865 },
    'Wilmington, DE': { lon: -75.5398, lat: 39.7391 },
    'Dover, DE': { lon: -75.5244, lat: 39.1582 },
    'Buffalo, NY': { lon: -78.8784, lat: 42.8864 },
    'Albany, NY': { lon: -73.7562, lat: 42.6526 },
    'Syracuse, NY': { lon: -76.1474, lat: 43.0481 },
    'Rochester, NY': { lon: -77.6088, lat: 43.1566 },
    'Yonkers, NY': { lon: -73.8987, lat: 40.9312 },
    'White Plains, NY': { lon: -73.7629, lat: 41.0340 },
    
    // New Jersey Cities
    'Newark, NJ': { lon: -74.1724, lat: 40.7357 },
    'Paterson, NJ': { lon: -74.1718, lat: 40.9168 },
    'Elizabeth, NJ': { lon: -74.2107, lat: 40.6640 },
    'Jersey City, NJ': { lon: -74.0431, lat: 40.7178 },
    'Edison, NJ': { lon: -74.4121, lat: 40.5187 },
    'Woodbridge, NJ': { lon: -74.2846, lat: 40.5576 },
    'Lakewood, NJ': { lon: -74.2179, lat: 40.0979 },
    'Toms River, NJ': { lon: -74.1979, lat: 39.9537 },
    'Hamilton, NJ': { lon: -74.6924, lat: 40.2236 },
    'Trenton, NJ': { lon: -74.7429, lat: 40.2171 },
    'Clifton, NJ': { lon: -74.1638, lat: 40.8584 },
    'Camden, NJ': { lon: -75.1196, lat: 39.9259 },
    'Brick, NJ': { lon: -74.1037, lat: 40.0576 },
    'Cherry Hill, NJ': { lon: -75.0307, lat: 39.9349 },
    'Passaic, NJ': { lon: -74.1285, lat: 40.8568 },
    'Union City, NJ': { lon: -74.0235, lat: 40.6976 },
    'Old Bridge, NJ': { lon: -74.3654, lat: 40.4148 },
    'Gloucester, NJ': { lon: -75.0318, lat: 39.8912 },
    'East Orange, NJ': { lon: -74.2049, lat: 40.7673 },
    'Bayonne, NJ': { lon: -74.1143, lat: 40.6687 },
    'Franklin, NJ': { lon: -74.5282, lat: 40.5273 },
    'North Bergen, NJ': { lon: -74.0121, lat: 40.8043 },
    'Vineland, NJ': { lon: -75.0257, lat: 39.4862 },
    'Piscataway, NJ': { lon: -74.4618, lat: 40.5548 },
    'New Brunswick, NJ': { lon: -74.4519, lat: 40.4862 },
    'Jackson, NJ': { lon: -74.3588, lat: 40.1062 },
    'Wayne, NJ': { lon: -74.2765, lat: 40.9254 },
    'Irvington, NJ': { lon: -74.2349, lat: 40.7323 },
    'Parsippany, NJ': { lon: -74.4257, lat: 40.8576 },
    'Howell, NJ': { lon: -74.1838, lat: 40.1476 },
    'Perth Amboy, NJ': { lon: -74.2654, lat: 40.5067 },
    'Hoboken, NJ': { lon: -74.0321, lat: 40.7439 },
    'Plainfield, NJ': { lon: -74.4074, lat: 40.6337 },
    'West New York, NJ': { lon: -74.0143, lat: 40.7879 },
    'Washington, NJ': { lon: -74.9771, lat: 40.7584 },
    'East Brunswick, NJ': { lon: -74.4160, lat: 40.4279 },
    'Bloomfield, NJ': { lon: -74.1854, lat: 40.8068 },
    'Bridgewater, NJ': { lon: -74.6099, lat: 40.5923 },
    'South Brunswick, NJ': { lon: -74.5390, lat: 40.3784 },
    'Middletown, NJ': { lon: -74.1237, lat: 40.3948 },
    'Montclair, NJ': { lon: -74.2090, lat: 40.8259 },
    'Kearny, NJ': { lon: -74.1454, lat: 40.7684 },
    'Linden, NJ': { lon: -74.2446, lat: 40.6223 },
    'Hackensack, NJ': { lon: -74.0435, lat: 40.8859 },
    'Sayreville, NJ': { lon: -74.3610, lat: 40.4595 },
    'Manalapan, NJ': { lon: -74.3363, lat: 40.2718 },
    'Atlantic City, NJ': { lon: -74.4229, lat: 39.3643 },
    'Fort Lee, NJ': { lon: -73.9701, lat: 40.8509 },
    'Fair Lawn, NJ': { lon: -74.1318, lat: 40.9403 },
    'Long Branch, NJ': { lon: -73.9924, lat: 40.3043 },
    'Princeton, NJ': { lon: -74.6672, lat: 40.3573 },
    'Englewood, NJ': { lon: -73.9726, lat: 40.8929 },
    'Freehold, NJ': { lon: -74.2735, lat: 40.2607 },
    'Garfield, NJ': { lon: -74.1132, lat: 40.8812 },
    'Morristown, NJ': { lon: -74.4815, lat: 40.7967 },
    'Dayton, NJ': { lon: -74.5121, lat: 40.3784 },
    'West Orange, NJ': { lon: -74.2390, lat: 40.7987 },
    'Rahway, NJ': { lon: -74.2777, lat: 40.6081 },
    'Marlboro, NJ': { lon: -74.2460, lat: 40.3151 },
    'Ewing, NJ': { lon: -74.7999, lat: 40.2698 },
    'Bergenfield, NJ': { lon: -73.9974, lat: 40.9276 },
    'Paramus, NJ': { lon: -74.0754, lat: 40.9445 },
    
    // Pennsylvania Cities
    'Philadelphia, PA': { lon: -75.1652, lat: 39.9526 },
    'Pittsburgh, PA': { lon: -79.9959, lat: 40.4406 },
    'Allentown, PA': { lon: -75.4714, lat: 40.6084 },
    'Erie, PA': { lon: -80.0852, lat: 42.1292 },
    'Reading, PA': { lon: -75.9268, lat: 40.3356 },
    'Scranton, PA': { lon: -75.6624, lat: 41.4090 },
    'Bethlehem, PA': { lon: -75.3705, lat: 40.6259 },
    'Lancaster, PA': { lon: -76.3055, lat: 40.0379 },
    'Harrisburg, PA': { lon: -76.8867, lat: 40.2732 },
    'York, PA': { lon: -76.7278, lat: 39.9626 },
    'State College, PA': { lon: -77.8600, lat: 40.7934 },
    'Wilkes-Barre, PA': { lon: -75.8813, lat: 41.2459 },
    'Chester, PA': { lon: -75.3557, lat: 39.8498 },
    'Norristown, PA': { lon: -75.3399, lat: 40.1215 },
    'King of Prussia, PA': { lon: -75.3860, lat: 40.0893 },
    'Lebanon, PA': { lon: -76.4113, lat: 40.3409 },
    'Hazleton, PA': { lon: -75.9746, lat: 40.9584 },
    'Easton, PA': { lon: -75.2207, lat: 40.6884 },
    'New Castle, PA': { lon: -80.3470, lat: 41.0037 },
    'Washington, PA': { lon: -80.2462, lat: 40.1740 },
    'Johnstown, PA': { lon: -78.9220, lat: 40.3267 },
    'McKeesport, PA': { lon: -79.8645, lat: 40.3478 },
    'Hermitage, PA': { lon: -80.4495, lat: 41.2334 },
    'Butler, PA': { lon: -79.8953, lat: 40.8612 },
    'Pottstown, PA': { lon: -75.6496, lat: 40.2454 },
    'Williamsport, PA': { lon: -77.0011, lat: 41.2412 },
    'Plum, PA': { lon: -79.7503, lat: 40.5006 },
    'Upper Darby, PA': { lon: -75.2927, lat: 39.9601 },
    'Monroeville, PA': { lon: -79.7881, lat: 40.4212 },
    'West Chester, PA': { lon: -75.6055, lat: 39.9606 },
    'Chambersburg, PA': { lon: -77.6611, lat: 39.9376 },
    'Phoenixville, PA': { lon: -75.5149, lat: 40.1304 },
    
    // Connecticut Cities
    'Stamford, CT': { lon: -73.5387, lat: 41.0534 },
    'Norwalk, CT': { lon: -73.4082, lat: 41.1176 },
    'Danbury, CT': { lon: -73.4540, lat: 41.3948 },
    'Waterbury, CT': { lon: -73.0515, lat: 41.5582 },
    
    // Delaware Cities
    'Newark, DE': { lon: -75.7497, lat: 39.6837 },
    
    // Maryland Cities
    'Baltimore, MD': { lon: -76.6122, lat: 39.2904 },
    'Annapolis, MD': { lon: -76.4922, lat: 38.9784 },
    'Frederick, MD': { lon: -77.4106, lat: 39.4143 },
    
    // Massachusetts Cities
    'Boston, MA': { lon: -71.0589, lat: 42.3601 },
    'Worcester, MA': { lon: -71.8023, lat: 42.2626 },
    'Springfield, MA': { lon: -72.5395, lat: 42.1015 },
    
    // Rhode Island
    'Providence, RI': { lon: -71.4128, lat: 41.8240 },
    
    // Virginia
    'Richmond, VA': { lon: -77.4360, lat: 37.5407 },
    'Norfolk, VA': { lon: -76.2859, lat: 36.8508 },
    'Virginia Beach, VA': { lon: -75.9780, lat: 36.8529 },
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle cargo weight with automatic comma formatting
    if (name === 'cargoWeight') {
      // Remove all non-digit characters
      const numericValue = value.replace(/[^\d]/g, '');
      // Format with commas
      const formattedValue = numericValue ? parseInt(numericValue).toLocaleString() : '';
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
      return;
    }
    
    // Real-time email validation
    if (name === 'customerEmail') {
      const validation = validateEmailComprehensive(value);
      setEmailValidation(validation);
    }
    
    // Auto-select Live Load when Export is selected
    if (name === 'shipmentType' && value === 'export') {
      setFormData({
        ...formData,
        [name]: value,
        unloadType: 'liveload' // Auto-select Live Load for export
      });
      return;
    }
    
    // Reset to Live Unload when Import is selected
    if (name === 'shipmentType' && value === 'import') {
      setFormData({
        ...formData,
        [name]: value,
        unloadType: 'live' // Default to Live Unload for import
      });
      return;
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

    // Filter locations for autocomplete
    if (name === 'pickupLocation') {
      const filtered = Object.keys(locations).filter(loc => 
        loc.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredPickupLocations(filtered);
      setShowPickupSuggestions(value.length > 0 && filtered.length > 0);
    }

    if (name === 'deliveryLocation') {
      const filtered = Object.keys(locations).filter(loc => 
        loc.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredDeliveryLocations(filtered);
      setShowDeliverySuggestions(value.length > 0 && filtered.length > 0);
    }
  };

  const selectLocation = (field, location) => {
    setFormData({ ...formData, [field]: location });
    if (field === 'pickupLocation') {
      setShowPickupSuggestions(false);
    } else {
      setShowDeliverySuggestions(false);
    }
  };

  // Note: Geocoding and distance calculation now handled by backend API

  const calculateRate = async () => {
    setCalculating(true);
    setError('');
    setRateResult(null);

    try {
      // Validate customer information
      if (!formData.customerName || !formData.customerEmail || !formData.customerCompany) {
        throw new Error('Please fill in all customer information fields (Name, Email, Company)');
      }
      
      // Comprehensive email validation
      const emailCheck = validateEmailComprehensive(formData.customerEmail);
      if (!emailCheck.isValid) {
        throw new Error(emailCheck.message || 'Please enter a valid email address');
      }
      
      // Validate shipment inputs
      if (!formData.pickupLocation || !formData.deliveryLocation) {
        throw new Error('Please enter both pickup and delivery locations');
      }
      if (!formData.containerType) {
        throw new Error('Please select a container type');
      }
      if (!formData.cargoWeight) {
        throw new Error('Please enter cargo weight');
      }

      // Convert weight to lbs (remove commas first)
      const numericWeight = formData.cargoWeight.replace(/,/g, '');
      let weightInLbs = parseFloat(numericWeight);
      if (formData.cargoWeightUnit === 'kg') {
        weightInLbs = weightInLbs * 2.20462; // kg to lbs
      } else if (formData.cargoWeightUnit === 'mt') {
        weightInLbs = weightInLbs * 2204.62; // metric tons to lbs
      }

      // Call backend API to calculate rate (always use standard service type)
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/calculate-rate`, {
        // Customer Information
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        company_name: formData.customerCompany,
        // Shipment Information
        pickup_location: formData.pickupLocation,
        delivery_location: formData.deliveryLocation,
        container_type: formData.containerType,
        service_type: 'standard',
        cargo_weight: Math.round(weightInLbs),
        unload_type: formData.unloadType || 'live',
        chassis_days: formData.chassisDays ? parseInt(formData.chassisDays) : 0,
        triaxle_days: formData.triaxleDays ? parseInt(formData.triaxleDays) : 0,
        storage_days: formData.storageDays ? parseInt(formData.storageDays) : 0,
        detention_hours: formData.detentionHours ? parseFloat(formData.detentionHours) : 0,
        overweight: formData.overweight,
        reefer: formData.reefer,
        port_liberty_bayonne_toll: formData.portLibertyBayonneToll,
        port_liberty_ny_toll: formData.portLibertyNYToll,
      });

      const result = response.data;

      // Format the result for display
      setRateResult({
        baseRate: result.base_rate,
        distance: result.distance.toFixed(1),
        distanceCharge: result.distance_charge.toFixed(2),
        serviceType: result.service_type,
        serviceMultiplier: result.service_multiplier,
        fuelSurcharge: result.fuel_surcharge.toFixed(2),
        accessorials: result.accessorials,
        totalRate: Math.round(result.total_rate),
        estimatedDuration: result.estimated_duration.toFixed(1),
        isEstimated: result.is_estimated,
        cargoWeight: Math.round(weightInLbs),
      });

      // Track successful quote calculation
      trackEvent('quote_calculated', {
        pickup: formData.pickupLocation,
        delivery: formData.deliveryLocation,
        container_type: formData.containerType,
        total_rate: Math.round(result.total_rate)
      });

      // Save the calculated quote to database for admin portal
      try {
        await axios.post(`${BACKEND_URL}/api/save-calculated-quote`, {
          customer_name: formData.customerName,
          customer_email: formData.customerEmail,
          company_name: formData.customerCompany,
          pickup_location: formData.pickupLocation,
          delivery_location: formData.deliveryLocation,
          container_type: formData.containerType,
          cargo_weight: Math.round(weightInLbs),
          service_type: result.service_type,
          base_rate: result.base_rate,
          distance: result.distance,
          distance_charge: result.distance_charge,
          fuel_surcharge: result.fuel_surcharge,
          accessorials: result.accessorials,
          total_rate: result.total_rate,
          estimated_duration: result.estimated_duration,
          is_estimated: result.is_estimated,
        });
        console.log('Quote saved successfully to admin portal');
      } catch (saveError) {
        console.error('Failed to save quote to admin portal:', saveError);
        // Don't show error to user - this is background operation
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(err.message || 'Failed to calculate rate. Please try again.');
      }
    } finally {
      setCalculating(false);
    }
  };

  const generatePDF = async () => {
    if (!rateResult) return;
    
    setGeneratingPDF(true);
    setPdfMessage('');
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/generate-quote-pdf`, {
        // Customer Information
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_company: formData.customerCompany,
        // Shipment Information
        pickup_location: formData.pickupLocation,
        delivery_location: formData.deliveryLocation,
        container_type: formData.containerType,
        cargo_weight: rateResult.cargoWeight,
        base_rate: rateResult.baseRate,
        distance: parseFloat(rateResult.distance),
        accessorials: rateResult.accessorials,
        total_rate: parseFloat(rateResult.totalRate),
        estimated_duration: parseFloat(rateResult.estimatedDuration),
      });
      
      setPdfFilename(response.data.filename);
      setPdfMessage('PDF generated successfully!');
    } catch (err) {
      setPdfMessage('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const downloadPDF = () => {
    if (!pdfFilename) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    window.open(`${BACKEND_URL}/api/download-quote-pdf/${pdfFilename}`, '_blank');
  };

  const handleEmailPDF = async () => {
    if (!pdfFilename || !emailAddress) return;
    
    setSendingEmail(true);
    setPdfMessage('');
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND_URL}/api/email-quote-pdf`, {
        email: emailAddress,
        pdf_filename: pdfFilename,
        pickup_location: formData.pickupLocation,
        delivery_location: formData.deliveryLocation,
        total_rate: parseFloat(rateResult.totalRate),
        customer_email: formData.customerEmail,  // Add customer email for reply-to
      });
      
      setPdfMessage(`Quote sent successfully to ${emailAddress}!`);
      setShowEmailDialog(false);
      setEmailAddress('');
    } catch (err) {
      setPdfMessage('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleBookNow = () => {
    setShowBookingModal(true);
    // Pre-fill customer phone if available
    setBookingData(prev => ({
      ...prev,
      customerPhone: formData.customerPhone || ''
    }));
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    // MANDATORY: Check if at least one container/trailer number is provided
    const validContainers = bookingData.containerTrailerNumbers.filter(c => c.number && c.number.trim());
    if (validContainers.length === 0) {
      alert('❌ Container/Trailer Number is REQUIRED. Please add at least one container or trailer number.');
      setSubmittingBooking(false);
      return;
    }
    
    // MANDATORY: Check if at least one BOL/Booking number is provided
    const validBOLs = bookingData.billOfLadingNumbers.filter(b => b.number && b.number.trim());
    if (validBOLs.length === 0) {
      alert('❌ Bill of Lading or Booking Number is REQUIRED. Please add at least one BOL or booking number.');
      setSubmittingBooking(false);
      return;
    }
    
    // Validate container numbers based on shipment type
    if (formData.shipmentType === 'import') {
      // For import: validate container number format (4 letters + 7 digits)
      const containerRegex = /^[A-Z]{4}\d{7}$/i;
      for (const container of validContainers) {
        if (!containerRegex.test(container.number.toUpperCase())) {
          alert('❌ For IMPORT shipments, container numbers must be in format: 4 letters followed by 7 digits (e.g., ABCD1234567)');
          setSubmittingBooking(false);
          return;
        }
      }
    }
    
    setSubmittingBooking(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/create-booking`, {
        // Customer Information
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        company_name: formData.customerCompany,
        phone: bookingData.customerPhone,
        notification_email: bookingData.notificationEmail,
        // Shipment Information
        pickup_location: formData.pickupLocation,
        delivery_location: formData.deliveryLocation,
        shipment_type: formData.shipmentType, // import, export, or trailer
        container_type: formData.containerType,
        cargo_weight: parseInt(formData.cargoWeight.toString().replace(/,/g, '')),
        service_type: formData.serviceType || 'standard',
        distance: parseFloat(rateResult.distance),
        // Rate Information
        base_rate: parseFloat(rateResult.baseRate),
        total_rate: parseFloat(rateResult.totalRate),
        accessorials: rateResult.accessorials,
        // Booking Specific Information - Vessel/Terminal
        vessel_eta: bookingData.vesselEta || null,
        terminal_receiving_date: bookingData.terminalReceivingDate || null,
        terminal_cut_off_date: bookingData.terminalCutOffDate || null,
        container_trailer_numbers: bookingData.containerTrailerNumbers
          .filter(c => c.number && c.number.trim())
          .map(c => ({number: c.number.trim(), status: 'pending'})),
        bill_of_lading_numbers: bookingData.billOfLadingNumbers
          .filter(b => b.number && b.number.trim())
          .map(b => ({number: b.number.trim(), status: 'pending'})),
        // Pickup & Delivery Schedule
        pickup_date: bookingData.pickupDate || null,
        pickup_time: bookingData.pickupTime || null,
        delivery_date: bookingData.deliveryDate || null,
        delivery_time: bookingData.deliveryTime || null,
        special_instructions: bookingData.specialInstructions || null,
        preferred_contact_method: bookingData.preferredContactMethod,
        additional_contact_person: bookingData.additionalContactPerson || null,
        quote_id: null // Can link to saved quote if needed
      });
      
      setBookingConfirmation(response.data);
      setShowBookingModal(false);
      
      // Reset booking form
      setBookingData({
        vesselEta: '',
        terminalReceivingDate: '',
        terminalCutOffDate: '',
        containerTrailerNumbers: [{number: ''}],
        billOfLadingNumbers: [{number: ''}],
        pickupDate: '',
        pickupTime: '',
        deliveryDate: '',
        deliveryTime: '',
        specialInstructions: '',
        preferredContactMethod: 'email',
        additionalContactPerson: '',
        customerPhone: '',
        notificationEmail: ''
      });
    } catch (err) {
      alert('Failed to create booking. Please try again.');
      console.error('Booking error:', err);
    } finally {
      setSubmittingBooking(false);
    }
  };

  // Step Navigation Functions
  const validateStep = (step) => {
    switch(step) {
      case 1: // Customer Information
        if (!formData.customerName || !formData.customerEmail) {
          setError('Please fill in your name and email');
          return false;
        }
        if (!emailValidation.isValid) {
          setError('Please enter a valid email address');
          return false;
        }
        break;
      case 2: // Route Information
        if (!formData.pickupLocation || !formData.deliveryLocation) {
          setError('Please select both pickup and delivery locations');
          return false;
        }
        break;
      case 3: // Container, Weight & Shipment Type
        if (!formData.shipmentType) {
          setError('Please select a shipment type');
          return false;
        }
        
        // Trailer shipments need to contact via email
        if (formData.shipmentType === 'trailer') {
          setError('Please email us at Rates@robandroberlytransport.com for trailer shipments');
          return false;
        }
        
        if (!formData.containerType || !formData.cargoWeight) {
          setError('Please select container type and enter cargo weight');
          return false;
        }
        if (parseInt(formData.cargoWeight.replace(/,/g, '')) <= 0) {
          setError('Cargo weight must be greater than 0');
          return false;
        }
        if (!formData.unloadType) {
          setError('Please select a load/unload type');
          return false;
        }
        break;
      default:
        break;
    }
    setError('');
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToStep = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (step > currentStep) {
      // Validate current step before moving forward
      if (validateStep(currentStep)) {
        setCurrentStep(step);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const getStepLabel = (step) => {
    const labels = {
      0: 'Email Verification',
      1: 'Customer Info',
      2: 'Route Details',
      3: 'Container, Weight & Shipment Type',
      4: 'Additional Services'
    };
    return labels[step];
  };



  // Email Verification Functions
  const checkEmailVerified = async (emailToCheck) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/verify-email/check`, {
        email: emailToCheck
      });
      return response.data.verified;
    } catch (err) {
      console.error('Failed to check email:', err);
      return false;
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setVerificationError('');

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setVerificationError('Please enter a valid email address');
      return;
    }

    try {
      setVerifying(true);
      
      // FIRST: Check if user exists in portal_users (existing customer)
      try {
        const portalCheckResponse = await axios.post(`${BACKEND_URL}/api/portal/check-email`, {
          email: email
        });
        
        if (portalCheckResponse.data.has_account) {
          // User exists in portal - get their profile data and auto-prefill
          console.log('✅ Existing customer detected, fetching profile...');
          
          try {
            // Fetch user profile data from portal_access_requests or calculated_quotes
            const profileResponse = await axios.get(`${BACKEND_URL}/api/portal/get-user-profile-data`, {
              params: { email: email }
            });
            
            const profileData = profileResponse.data;
            
            // Auto-prefill form with user's existing data
            setFormData(prev => ({
              ...prev,
              customerEmail: email,
              customerName: profileData.name || '',
              customerCompany: profileData.company_name || '',
              customerPhone: profileData.phone || ''
            }));
            
            console.log('✅ Profile data auto-filled for existing customer');
          } catch (profileErr) {
            console.warn('Could not fetch profile data, continuing with email only');
            setFormData(prev => ({ ...prev, customerEmail: email }));
          }
          
          // Skip email verification for existing customers
          setEmailVerified(true);
          const validation = validateEmailComprehensive(email);
          setEmailValidation(validation);
          setCurrentStep(1);
          return;
        }
      } catch (portalErr) {
        console.log('Not an existing portal user, checking email verification...');
      }
      
      // SECOND: Check if email is already verified (for non-portal users)
      const isVerified = await checkEmailVerified(email);
      
      if (isVerified) {
        // Email already verified, proceed to next step
        setEmailVerified(true);
        setFormData(prev => ({ ...prev, customerEmail: email }));
        // Validate and set email validation state
        const validation = validateEmailComprehensive(email);
        setEmailValidation(validation);
        setCurrentStep(1);
        return;
      }

      // THIRD: Send verification code for new users
      const response = await axios.post(`${BACKEND_URL}/api/verify-email/send-code`, {
        email: email
      });

      if (response.data.success) {
        setCodeSent(true);
        setVerificationError('');
      }
    } catch (err) {
      setVerificationError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setVerificationError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Please enter the 6-digit code');
      return;
    }

    try {
      setVerifying(true);

      const response = await axios.post(`${BACKEND_URL}/api/verify-email/verify-code`, {
        email: email,
        code: verificationCode
      });

      if (response.data.verified) {
        setEmailVerified(true);
        setFormData(prev => ({ ...prev, customerEmail: email }));
        // Validate and set email validation state
        const validation = validateEmailComprehensive(email);
        setEmailValidation(validation);
        setCurrentStep(1); // Move to first actual form step
      }
    } catch (err) {
      setVerificationError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const resendCode = async () => {
    setVerificationError('');
    try {
      await axios.post(`${BACKEND_URL}/api/verify-email/send-code`, {
        email: email
      });
      setVerificationError('');
      alert('New verification code sent!');
    } catch (err) {
      setVerificationError('Failed to resend code');
    }
  };

  // Analytics Tracking
  const trackEvent = async (eventType, metadata = {}) => {
    try {
      await axios.post(`${BACKEND_URL}/api/analytics/track`, {
        event_type: eventType,
        page: '/get-quote',
        user_email: formData.customerEmail || null,
        session_id: sessionId,
        metadata: metadata
      });
    } catch (err) {
      // Silent fail - don't interrupt user experience
      console.log('Analytics tracking failed:', err);
    }
  };

  // Track form start on component mount
  React.useEffect(() => {
    trackEvent('form_start', { step: 1 });
  }, []);

  // Track step changes
  React.useEffect(() => {
    if (currentStep > 1) {
      trackEvent('form_progress', { 
        step: currentStep, 
        step_name: getStepLabel(currentStep) 
      });
    }
  }, [currentStep]);

  // Track form abandonment on unmount
  React.useEffect(() => {
    return () => {
      // Only track abandonment if form not completed
      if (!rateResult && currentStep < totalSteps) {
        trackEvent('form_abandon', { 
          last_step: currentStep,
          step_name: getStepLabel(currentStep)
        });
      }
    };
  }, [rateResult, currentStep]);

  return (
    <div className="max-w-6xl mx-auto" data-testid="rate-calculator">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Calculator Form */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold mb-4">Calculate Your Shipping Rate</h2>
          
          {/* Progress Indicator */}
          <div className="mb-6">
            {/* Progress Bar */}
            <div className="relative">
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                <div
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600 transition-all duration-500"
                ></div>
              </div>
            </div>

            {/* Step Indicators - Only show after email verification */}
            {currentStep > 0 && (
              <div className="flex justify-between mb-2">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    onClick={() => goToStep(step)}
                    className={`flex-1 text-center cursor-pointer transition-all ${
                      step <= currentStep ? 'opacity-100' : 'opacity-50'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mb-1 transition-all ${
                          step < currentStep
                            ? 'bg-green-500 text-white'
                            : step === currentStep
                            ? 'bg-blue-600 text-white ring-4 ring-blue-200'
                            : 'bg-gray-300 text-gray-600'
                        }`}
                      >
                        {step < currentStep ? '✓' : step}
                      </div>
                      <span className={`text-xs font-medium ${
                        step === currentStep ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {getStepLabel(step)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Current Step Info */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Step <span className="font-bold text-blue-600">{currentStep}</span> of {totalSteps}
              </p>
            </div>
          </div>
          
          <div className="space-y-3">

            {/* Step 0: Email Verification */}
            {currentStep === 0 && (
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold mb-3 text-blue-900">📧 Email Verification Required</h3>
                <p className="text-sm text-gray-700 mb-4">
                  To ensure accurate quotes and prevent spam, we need to verify your email address before showing pricing.
                </p>

                {verificationError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                    {verificationError}
                  </div>
                )}

                {!codeSent ? (
                  <form onSubmit={handleEmailSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your.email@company.com"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Returning customer? We'll recognize your email and skip verification.
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={verifying}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {verifying ? 'Checking...' : 'Continue to Quote →'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyCode} className="space-y-4">
                    <div className="bg-green-50 border border-green-200 px-4 py-3 rounded text-sm text-green-800 mb-4">
                      ✅ Verification code sent to <strong>{email}</strong>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Enter 6-Digit Code *
                      </label>
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength="6"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-center text-2xl font-mono tracking-widest"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Code expires in 10 minutes
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={verifying || verificationCode.length !== 6}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {verifying ? 'Verifying...' : 'Verify & Continue →'}
                    </button>

                    <div className="flex justify-between text-sm">
                      <button
                        type="button"
                        onClick={() => setCodeSent(false)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        ← Change Email
                      </button>
                      <button
                        type="button"
                        onClick={resendCode}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Resend Code
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}


            {/* Step 1: Customer Information */}
            {currentStep === 1 && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
              <h3 className="text-base font-semibold mb-3 text-blue-900">Step 1: Customer Information</h3>
              <div className="space-y-2">
                {/* Customer Name - Full Width */}
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>

                {/* Email and Company - Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {/* Customer Email */}
                  <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <div className="relative">
                    <input
                      type="email"
                      name="customerEmail"
                      value={formData.customerEmail}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:border-transparent ${
                        formData.customerEmail && emailValidation.isValid
                          ? 'border-green-500 focus:ring-green-500'
                          : formData.customerEmail && emailValidation.showWarning
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-blue-600'
                      }`}
                    />
                    {formData.customerEmail && emailValidation.isValid && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <span className="text-green-500 text-xl">✓</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Validation messages */}
                  {formData.customerEmail && emailValidation.showWarning && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-700 font-medium">
                        {emailValidation.message}
                      </p>
                      {emailValidation.suggestion && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, customerEmail: emailValidation.suggestion });
                            const validation = validateEmailComprehensive(emailValidation.suggestion);
                            setEmailValidation(validation);
                          }}
                          className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                          Use: {emailValidation.suggestion}
                        </button>
                      )}
                    </div>
                  )}
                  
                  {formData.customerEmail && emailValidation.isValid && (
                    <p className="mt-1 text-sm text-green-600">
                      ✓ {emailValidation.message}
                    </p>
                  )}
                </div>

                  {/* Customer Company */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Company *</label>
                    <input
                      type="text"
                      name="customerCompany"
                      value={formData.customerCompany}
                      onChange={handleChange}
                      placeholder="Company Name"
                      required
                      className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Phone Number - Full Width */}
                <div className="mt-2">
                  <label className="block text-sm font-medium mb-1">Phone (Optional)</label>
                  <input
                    type="tel"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleChange}
                    placeholder="(xxx) xxx-xxxx"
                    className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Step Navigation for Step 1 */}
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Next: Route Details →
                </button>
              </div>
            </div>
            )}

            {/* Step 2: Route Information */}
            {currentStep === 2 && (
            <div>
            <h3 className="text-base font-semibold mt-2 mb-3 text-blue-900">Step 2: Shipment Information</h3>
            <h3 className="text-base font-semibold mt-4 mb-2">Shipment Information</h3>
            
            {/* Pickup Location */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Pickup Location *</label>
              <input
                type="text"
                name="pickupLocation"
                value={formData.pickupLocation}
                onChange={handleChange}
                onFocus={() => {
                  if (formData.pickupLocation.length > 0) {
                    const filtered = Object.keys(locations).filter(loc => 
                      loc.toLowerCase().includes(formData.pickupLocation.toLowerCase())
                    );
                    setFilteredPickupLocations(filtered);
                    setShowPickupSuggestions(filtered.length > 0);
                  }
                }}
                onBlur={() => setTimeout(() => setShowPickupSuggestions(false), 200)}
                placeholder="Enter address or terminal"
                className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="pickup-location"
              />
              
              {/* Autocomplete Suggestions */}
              {showPickupSuggestions && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredPickupLocations.length > 0 ? (
                    <>
                      {/* Terminals */}
                      {filteredPickupLocations.filter(loc => loc.includes(':')).length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-700">
                            TERMINALS
                          </div>
                          {filteredPickupLocations.filter(loc => loc.includes(':')).map((loc) => (
                            <div
                              key={loc}
                              onClick={() => selectLocation('pickupLocation', loc)}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b"
                            >
                              {loc}
                            </div>
                          ))}
                        </>
                      )}
                      
                      {/* Ports */}
                      {filteredPickupLocations.filter(loc => loc.includes('Port') && !loc.includes(':')).length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-700">
                            MAJOR PORTS
                          </div>
                          {filteredPickupLocations.filter(loc => loc.includes('Port') && !loc.includes(':')).map((loc) => (
                            <div
                              key={loc}
                              onClick={() => selectLocation('pickupLocation', loc)}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b"
                            >
                              {loc}
                            </div>
                          ))}
                        </>
                      )}
                      
                      {/* Cities */}
                      {filteredPickupLocations.filter(loc => !loc.includes('Port') && !loc.includes(':')).length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-700">
                            CITIES
                          </div>
                          {filteredPickupLocations.filter(loc => !loc.includes('Port') && !loc.includes(':')).map((loc) => (
                            <div
                              key={loc}
                              onClick={() => selectLocation('pickupLocation', loc)}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b"
                            >
                              {loc}
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No matches found. You can enter any address manually.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Delivery Location */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Delivery Location *</label>
              <input
                type="text"
                name="deliveryLocation"
                value={formData.deliveryLocation}
                onChange={handleChange}
                onFocus={() => {
                  if (formData.deliveryLocation.length > 0) {
                    const filtered = Object.keys(locations).filter(loc => 
                      loc.toLowerCase().includes(formData.deliveryLocation.toLowerCase())
                    );
                    setFilteredDeliveryLocations(filtered);
                    setShowDeliverySuggestions(filtered.length > 0);
                  }
                }}
                onBlur={() => setTimeout(() => setShowDeliverySuggestions(false), 200)}
                placeholder="Enter address or city"
                className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="delivery-location"
              />
              
              {/* Autocomplete Suggestions */}
              {showDeliverySuggestions && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredDeliveryLocations.length > 0 ? (
                    <>
                      {/* Terminals */}
                      {filteredDeliveryLocations.filter(loc => loc.includes(':')).length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-700">
                            TERMINALS
                          </div>
                          {filteredDeliveryLocations.filter(loc => loc.includes(':')).map((loc) => (
                            <div
                              key={loc}
                              onClick={() => selectLocation('deliveryLocation', loc)}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b"
                            >
                              {loc}
                            </div>
                          ))}
                        </>
                      )}
                      
                      {/* Ports */}
                      {filteredDeliveryLocations.filter(loc => loc.includes('Port') && !loc.includes(':')).length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-700">
                            MAJOR PORTS
                          </div>
                          {filteredDeliveryLocations.filter(loc => loc.includes('Port') && !loc.includes(':')).map((loc) => (
                            <div
                              key={loc}
                              onClick={() => selectLocation('deliveryLocation', loc)}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b"
                            >
                              {loc}
                            </div>
                          ))}
                        </>
                      )}
                      
                      {/* Cities */}
                      {filteredDeliveryLocations.filter(loc => !loc.includes('Port') && !loc.includes(':')).length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-700">
                            CITIES
                          </div>
                          {filteredDeliveryLocations.filter(loc => !loc.includes('Port') && !loc.includes(':')).map((loc) => (
                            <div
                              key={loc}
                              onClick={() => selectLocation('deliveryLocation', loc)}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b"
                            >
                              {loc}
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No matches found. You can enter any address manually.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step Navigation for Step 2 */}
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={prevStep}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Next: Container Info →
              </button>
            </div>
           </div>
            )}

            {/* Step 3: Container, Weight & Shipment Type */}
            {currentStep === 3 && (
            <div>
            <h3 className="text-base font-semibold mt-2 mb-3 text-blue-900">Step 3: Container, Weight & Shipment Type</h3>
            
            {/* Shipment Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Shipment Type *</label>
              <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="shipmentType"
                    value="import"
                    checked={formData.shipmentType === 'import'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm">Import</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="shipmentType"
                    value="export"
                    checked={formData.shipmentType === 'export'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm">Export</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="shipmentType"
                    value="trailer"
                    checked={formData.shipmentType === 'trailer'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm">Trailer</span>
                </label>
              </div>
            </div>

            {/* Trailer Message */}
            {formData.shipmentType === 'trailer' && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  📧 For trailer shipments, please email us at{' '}
                  <a href="mailto:Rates@robandroberlytransport.com" className="font-semibold underline">
                    Rates@robandroberlytransport.com
                  </a>{' '}
                  for assistance.
                </p>
              </div>
            )}

            {/* Container Type */}
            {formData.shipmentType !== 'trailer' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Container Type *</label>
                <select
                  name="containerType"
                  value={formData.containerType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  data-testid="container-type"
                >
                  <option value="">Select container type</option>
                  {containerTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Cargo Weight */}
            {formData.shipmentType !== 'trailer' && (
              <div className="relative mb-4">
                <label className="block text-sm font-medium mb-1">Cargo Weight *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="cargoWeight"
                    value={formData.cargoWeight}
                    onChange={handleChange}
                    placeholder="Enter weight"
                    required
                    className="flex-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    data-testid="cargo-weight"
                  />
                  <select
                    name="cargoWeightUnit"
                    value={formData.cargoWeightUnit}
                    onChange={handleChange}
                    className="px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    data-testid="cargo-weight-unit"
                  >
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                    <option value="mt">mt</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Standard max: 44,000 lbs
                </p>
              </div>
            )}

            {/* Load/Unload Type */}
            {formData.shipmentType !== 'trailer' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Load/Unload Type *</label>
                <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                  {/* Live Load */}
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="unloadType"
                      value="liveload"
                      checked={formData.unloadType === 'liveload'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm">Live Load</span>
                  </label>
                  
                  {/* Live Unload */}
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="unloadType"
                      value="live"
                      checked={formData.unloadType === 'live'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm">Live Unload</span>
                  </label>
                  
                  {/* Drop Off */}
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="unloadType"
                      value="drop"
                      checked={formData.unloadType === 'drop'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm">Drop Off</span>
                  </label>
                </div>
                
                {formData.unloadType === 'drop' && (
                  <p className="text-xs text-blue-600 mt-2">
                    💡 Drop fee (50% of base rate) will be added to your quote depending on the volume!
                  </p>
                )}
              </div>
            )}

            {/* Step Navigation for Step 3 */}
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={prevStep}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={formData.shipmentType === 'trailer'}
                className={`px-6 py-2 rounded-lg transition font-medium ${
                  formData.shipmentType === 'trailer'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {formData.shipmentType === 'trailer' ? 'Contact us for trailer quotes' : 'Next: Additional Services →'}
              </button>
            </div>
           </div>
            )}

            {/* Step 4: Additional Services */}
            {currentStep === 4 && (
            <div>
            <h3 className="text-base font-semibold mt-2 mb-3 text-blue-900">Step 4: Additional Services (Optional)</h3>

            {/* Accessorial Services */}
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-3">Accessorial Services</label>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Chassis */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Chassis ($40/day)</label>
                  <input
                    type="number"
                    name="chassisDays"
                    value={formData.chassisDays}
                    onChange={handleChange}
                    placeholder="Number of days"
                    min="0"
                    step="1"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    data-testid="chassis-days"
                  />
                </div>

                {/* Triaxle Chassis */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Triaxle Chassis ($75/day)</label>
                  <input
                    type="number"
                    name="triaxleDays"
                    value={formData.triaxleDays}
                    onChange={handleChange}
                    placeholder="Number of days"
                    min="0"
                    step="1"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    data-testid="triaxle-days"
                  />
                </div>

                {/* Storage */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Storage ($35/day)</label>
                  <input
                    type="number"
                    name="storageDays"
                    value={formData.storageDays}
                    onChange={handleChange}
                    placeholder="Number of days"
                    min="0"
                    step="1"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    data-testid="storage-days"
                  />
                </div>

                {/* Detention */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Detention ($75/hr)</label>
                  <input
                    type="number"
                    name="detentionHours"
                    value={formData.detentionHours}
                    onChange={handleChange}
                    placeholder="Number of hours"
                    min="0"
                    step="0.5"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    data-testid="detention-hours"
                  />
                </div>

                {/* Overweight */}
                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    name="overweight"
                    checked={formData.overweight}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 rounded"
                    data-testid="overweight-checkbox"
                  />
                  <label className="ml-2 text-sm">Overweight (+$150)</label>
                </div>

                {/* Reefer */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="reefer"
                    checked={formData.reefer}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 rounded"
                    data-testid="reefer-checkbox"
                  />
                  <label className="ml-2 text-sm">Reefer (+$150)</label>
                </div>

                {/* Port Liberty Bayonne Toll */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="portLibertyBayonneToll"
                    checked={formData.portLibertyBayonneToll}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 rounded"
                    data-testid="bayonne-toll-checkbox"
                  />
                  <label className="ml-2 text-sm">Port Liberty Bayonne Toll (+$115)</label>
                </div>

                {/* Port Liberty New York Toll */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="portLibertyNYToll"
                    checked={formData.portLibertyNYToll}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 rounded"
                    data-testid="ny-toll-checkbox"
                  />
                  <label className="ml-2 text-sm">Port Liberty New York Toll (+$225)</label>
                </div>
              </div>
            </div>

            {/* Step Navigation for Step 4 */}
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={prevStep}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                ← Back
              </button>
              <button
                onClick={calculateRate}
                disabled={calculating}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:bg-gray-400"
                data-testid="calculate-rate-btn"
              >
                {calculating ? 'Calculating...' : '🚀 Calculate Rate'}
              </button>
            </div>
           </div>
            )}
            {!rateResult && !calculating && (
              <p className="text-xs text-gray-500 text-center mt-2">
                ℹ️ Rate breakdown will display on the right after calculation
              </p>
            )}
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" data-testid="rate-error">
              {error}
            </div>
          )}
        </div>

        {/* Rate Result */}
        <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold mb-6">Rate Breakdown</h2>
          
          {!rateResult && !error && (
            <div className="text-center py-8">
              <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <div className="space-y-2">
                <p className="text-gray-600 font-medium">Fill in the form and click "Calculate Rate"</p>
                <p className="text-gray-500 text-sm">Your rate breakdown will be displayed here →</p>
              </div>
            </div>
          )}

          {rateResult && (
            <div className="space-y-4" data-testid="rate-result">
              <div className="bg-white p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Base Rate:</span>
                  <span className="font-medium">${rateResult.baseRate.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Distance:</span>
                  <span className="font-medium">{rateResult.distance} miles</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Cargo Weight:</span>
                  <span className="font-medium">{rateResult.cargoWeight ? rateResult.cargoWeight.toLocaleString() : '0'} lbs</span>
                </div>
                
                {/* Only show applied accessorials */}
                {rateResult.accessorials.length > 0 && (
                  <div className="border-t pt-3 mt-3">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Applied Accessorials:</div>
                    {rateResult.accessorials.map((acc, index) => (
                      <div key={index} className="flex justify-between mb-2 pl-2">
                        <span className="text-gray-600 text-sm">• {acc.name}:</span>
                        <span className="font-medium text-sm">${acc.amount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-600 text-white p-6 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">Total Rate:</span>
                  <span className="text-3xl font-bold">${rateResult.totalRate}</span>
                </div>
                {rateResult.isEstimated && (
                  <p className="text-blue-100 text-xs mt-2">* Rate based on estimated distance</p>
                )}
              </div>

              {/* Drop Fee Note - shown if Drop Off is selected */}
              {formData.unloadType === 'drop' && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>📦 Drop Fee Notice:</strong> Drop fee (50% of base rate) will be added to your quote plus daily chassis usage.
                  </p>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> This is an estimated quote. Final pricing may vary based on actual conditions, traffic, and availability. Contact us for a detailed quote.
                </p>
              </div>

              {/* PDF Actions */}
              <div className="bg-white p-6 rounded-lg border-2 border-blue-200 mt-4">
                <h3 className="font-bold text-lg mb-4 text-gray-800">Save or Share Your Quote</h3>
                
                {pdfMessage && (
                  <div className={`mb-4 p-3 rounded ${pdfMessage.includes('success') || pdfMessage.includes('sent') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {pdfMessage}
                  </div>
                )}
                
                <div className="flex flex-wrap gap-3">
                  {!pdfFilename ? (
                    <button
                      onClick={generatePDF}
                      disabled={generatingPDF}
                      className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {generatingPDF ? 'Generating PDF...' : 'Generate PDF'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={downloadPDF}
                        className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download PDF
                      </button>
                      
                      <button
                        onClick={() => {
                          setEmailAddress(formData.customerEmail);
                          setShowEmailDialog(true);
                        }}
                        className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email PDF
                      </button>
                      
                      <button
                        onClick={() => {
                          setPdfFilename(null);
                          setPdfMessage('');
                        }}
                        className="flex items-center gap-2 bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition"
                      >
                        Generate New PDF
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Book Now Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-300 mt-4">
                <h3 className="font-bold text-lg mb-3 text-gray-800 flex items-center">
                  <span className="mr-2">📅</span>
                  Ready to Book?
                </h3>
                <p className="text-sm text-gray-700 mb-4">
                  Secure your shipment now! Fill in your vessel and pickup details to create a booking request.
                </p>
                <button
                  onClick={handleBookNow}
                  className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition font-semibold text-lg shadow-lg hover:shadow-xl"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Book Now
                </button>
              </div>

              {/* Email Dialog */}
              {showEmailDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full">
                    <h3 className="text-xl font-bold mb-4">Email Quote</h3>
                    <p className="text-gray-600 mb-4">Enter your email address to receive the quote:</p>
                    
                    <input
                      type="email"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="your.email@example.com"
                      className="w-full px-4 py-2 border rounded-md mb-4 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                    
                    <div className="flex gap-3">
                      <button
                        onClick={handleEmailPDF}
                        disabled={!emailAddress || sendingEmail}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                      </button>
                      <button
                        onClick={() => {
                          setShowEmailDialog(false);
                          setEmailAddress('');
                        }}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Booking Modal */}
              {showBookingModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
                  <div className="flex min-h-screen items-start justify-center p-4 py-8">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8">
                      <h3 className="text-2xl font-bold mb-4 text-gray-800">Create Booking Request</h3>
                      <p className="text-gray-600 mb-6">Fill in the details below to submit your booking request.</p>
                    
                    <form onSubmit={handleBookingSubmit} className="space-y-4">
                      {/* Vessel/Terminal Information */}
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-gray-800 mb-3">Vessel/Terminal Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Vessel ETA (Optional)</label>
                            <input
                              type="date"
                              value={bookingData.vesselEta}
                              onChange={(e) => setBookingData({...bookingData, vesselEta: e.target.value})}
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Terminal Receiving Date (Optional)</label>
                            <input
                              type="date"
                              value={bookingData.terminalReceivingDate}
                              onChange={(e) => setBookingData({...bookingData, terminalReceivingDate: e.target.value})}
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Terminal Cut Off Date (Optional)</label>
                            <input
                              type="date"
                              value={bookingData.terminalCutOffDate}
                              onChange={(e) => setBookingData({...bookingData, terminalCutOffDate: e.target.value})}
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1">
                              <span className="text-red-600">* </span>
                              {formData.shipmentType === 'import' ? 'Container Numbers (Required)' : 
                               formData.shipmentType === 'export' ? 'Container/Trailer Numbers (Required)' : 
                               'Container/Trailer Numbers (Required)'}
                            </label>
                            {formData.shipmentType === 'import' && (
                              <p className="text-xs text-blue-600 mb-2">
                                📋 Format: 4 letters followed by 7 digits (e.g., ABCD1234567)
                              </p>
                            )}
                            <p className="text-xs text-gray-600 mb-2">
                              ⚠️ At least one container/trailer number is required to create a booking
                            </p>
                            {bookingData.containerTrailerNumbers.map((container, index) => (
                              <div key={index} className="flex gap-2 mb-2">
                                <input
                                  type="text"
                                  value={container.number}
                                  onChange={(e) => {
                                    const updated = [...bookingData.containerTrailerNumbers];
                                    updated[index].number = e.target.value.toUpperCase();
                                    setBookingData({...bookingData, containerTrailerNumbers: updated});
                                  }}
                                  placeholder={formData.shipmentType === 'import' ? 'ABCD1234567' : 'Container/Trailer Number'}
                                  required={formData.shipmentType === 'import'}
                                  pattern={formData.shipmentType === 'import' ? '[A-Z]{4}\\d{7}' : undefined}
                                  maxLength={formData.shipmentType === 'import' ? 11 : undefined}
                                  className="flex-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600"
                                />
                                {bookingData.containerTrailerNumbers.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = bookingData.containerTrailerNumbers.filter((_, i) => i !== index);
                                      setBookingData({...bookingData, containerTrailerNumbers: updated});
                                    }}
                                    className="px-3 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...bookingData.containerTrailerNumbers, {number: ''}];
                                setBookingData({...bookingData, containerTrailerNumbers: updated});
                              }}
                              className="mt-2 px-3 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                            >
                              + Add Container
                            </button>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1">
                              <span className="text-red-600">* </span>
                              {formData.shipmentType === 'import' ? 'Bill of Lading (BOL) Numbers (Required)' : 
                               formData.shipmentType === 'export' ? 'Booking Numbers (Required)' : 
                               'Bill of Lading/Booking Numbers (Required)'}
                            </label>
                            <p className="text-xs text-gray-600 mb-2">
                              ⚠️ At least one {formData.shipmentType === 'export' ? 'booking number' : 'BOL number'} is required to create a booking
                            </p>
                            {bookingData.billOfLadingNumbers.map((bol, index) => (
                              <div key={index} className="flex gap-2 mb-2">
                                <input
                                  type="text"
                                  value={bol.number}
                                  onChange={(e) => {
                                    const updated = [...bookingData.billOfLadingNumbers];
                                    updated[index].number = e.target.value;
                                    setBookingData({...bookingData, billOfLadingNumbers: updated});
                                  }}
                                  placeholder={
                                    formData.shipmentType === 'import' ? 'e.g., BOL123456' : 
                                    formData.shipmentType === 'export' ? 'e.g., BKG789012' : 
                                    'BOL or Booking Number'
                                  }
                                  required
                                  className="flex-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-600"
                                />
                                {bookingData.billOfLadingNumbers.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = bookingData.billOfLadingNumbers.filter((_, i) => i !== index);
                                      setBookingData({...bookingData, billOfLadingNumbers: updated});
                                    }}
                                    className="px-3 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...bookingData.billOfLadingNumbers, {number: ''}];
                                setBookingData({...bookingData, billOfLadingNumbers: updated});
                              }}
                              className="mt-2 px-3 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                            >
                              {formData.shipmentType === 'import' ? '+ Add BOL' : 
                               formData.shipmentType === 'export' ? '+ Add Booking Number' : 
                               '+ Add Number'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Pickup & Delivery Schedule */}
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-gray-800 mb-3">Pickup & Delivery Schedule</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Pickup Date (Optional)</label>
                            <input
                              type="date"
                              value={bookingData.pickupDate}
                              onChange={(e) => setBookingData({...bookingData, pickupDate: e.target.value})}
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-green-600"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Pickup Time (Optional)</label>
                            <input
                              type="time"
                              value={bookingData.pickupTime}
                              onChange={(e) => setBookingData({...bookingData, pickupTime: e.target.value})}
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-green-600"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Delivery Date (Optional)</label>
                            <input
                              type="date"
                              value={bookingData.deliveryDate}
                              onChange={(e) => setBookingData({...bookingData, deliveryDate: e.target.value})}
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-green-600"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Delivery Time (Optional)</label>
                            <input
                              type="time"
                              value={bookingData.deliveryTime}
                              onChange={(e) => setBookingData({...bookingData, deliveryTime: e.target.value})}
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-green-600"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1">Special Instructions (Optional)</label>
                            <textarea
                              value={bookingData.specialInstructions}
                              onChange={(e) => setBookingData({...bookingData, specialInstructions: e.target.value})}
                              placeholder="Any special handling requirements or instructions..."
                              rows="2"
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-green-600"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Preferred Contact Method *</label>
                            <select
                              value={bookingData.preferredContactMethod}
                              onChange={(e) => setBookingData({...bookingData, preferredContactMethod: e.target.value})}
                              required
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-green-600"
                            >
                              <option value="email">Email</option>
                              <option value="phone">Phone</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Additional Contact Person (Optional)</label>
                            <input
                              type="text"
                              value={bookingData.additionalContactPerson}
                              onChange={(e) => setBookingData({...bookingData, additionalContactPerson: e.target.value})}
                              placeholder="Name and phone number"
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-green-600"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Contact Information */}
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <h4 className="font-semibold text-gray-800 mb-3">Contact Information for Updates</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Phone Number *</label>
                            <input
                              type="tel"
                              value={bookingData.customerPhone}
                              onChange={(e) => setBookingData({...bookingData, customerPhone: e.target.value})}
                              placeholder="(xxx) xxx-xxxx"
                              required
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-purple-600"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Email for Updates *</label>
                            <input
                              type="email"
                              value={bookingData.notificationEmail}
                              onChange={(e) => setBookingData({...bookingData, notificationEmail: e.target.value})}
                              placeholder="your@email.com"
                              required
                              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-purple-600"
                            />
                            <p className="text-xs text-gray-600 mt-1">Where should we send booking updates?</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3 mt-6">
                        <button
                          type="submit"
                          disabled={submittingBooking}
                          className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                        >
                          {submittingBooking ? 'Submitting...' : 'Submit Booking Request'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowBookingModal(false)}
                          className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                    </div>
                  </div>
                </div>
              )}

              {/* Booking Confirmation Modal */}
              {bookingConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
                  <div className="flex min-h-screen items-center justify-center p-4">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full">
                    <div className="text-center">
                      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                        <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Booking Request Submitted!</h3>
                      <p className="text-gray-600 mb-2">
                        Your booking reference: <strong className="text-blue-600">{bookingConfirmation.booking_reference}</strong>
                      </p>
                      <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg mb-4 text-left">
                        <p className="text-sm font-semibold text-yellow-800 mb-2">
                          ⚠️  STATUS: PENDING - NOT YET CONFIRMED
                        </p>
                        <p className="text-xs text-gray-700">
                          This booking is NOT confirmed until a company representative contacts you.
                        </p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6 text-left">
                        <p className="text-sm text-gray-700">
                          ✅ Confirmation email sent to <strong>{bookingConfirmation.notification_email}</strong><br/>
                          ✅ Admin notification sent to rates@robandroberlytransport.com<br/>
                          ✅ All future updates will be sent to <strong>{bookingConfirmation.notification_email}</strong><br/>
                          ✅ Our team will review your booking request<br/>
                          ✅ You'll receive a confirmation call/email within 24 hours
                        </p>
                      </div>
                      <button
                        onClick={() => setBookingConfirmation(null)}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
                      >
                        Close
                      </button>
                    </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* How to Use */}
      <div className="mt-8 bg-blue-50 border border-blue-200 p-6 rounded-lg">
        <h3 className="font-bold text-lg mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          How to Enter Addresses
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold mb-2 text-blue-900">Option 1: Select from Suggestions</h4>
            <p className="text-sm text-gray-700 mb-2">Start typing and choose from our predefined terminals, ports, and cities. This provides the fastest and most accurate results.</p>
            <p className="text-xs text-gray-600 italic">Example: Type "APM" to see APM Terminal</p>
          </div>
          <div>
            <h4 className="font-semibold mb-2 text-blue-900">Option 2: Enter Custom Address</h4>
            <p className="text-sm text-gray-700 mb-2">Type any full US address with street, city, state, and ZIP code. Our system will automatically geocode the location.</p>
            <p className="text-xs text-gray-600 italic">Example: "123 Main Street, Newark, NJ 07102"</p>
          </div>
        </div>
        <div className="mt-4 p-3 bg-white rounded border border-blue-200">
          <p className="text-sm text-gray-700">
            <strong>💡 Tip:</strong> For most accurate results, include the complete address with ZIP code. The system uses OpenRouteService API to calculate real driving distances between locations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RateCalculator;
